
import React from 'react';
import { format } from 'date-fns';
import { Download, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useOptimizedFinanceProcurements } from '@/hooks/use-optimized-finance-procurements';
import { calculateTotalAmount } from '@/utils/procurementCalculations';

import FinanceEmptyState from './FinanceEmptyState';
import FinanceProcurementsSearch from './FinanceProcurementsSearch';
import FinanceProcurementsTable from './FinanceProcurementsTable';
import FinancePagination from './FinancePagination';
import * as XLSX from 'xlsx';

interface OptimizedFinanceProcurementListProps {
  status?: 'pending' | 'approved' | 'rejected';
}

const OptimizedFinanceProcurementList: React.FC<OptimizedFinanceProcurementListProps> = ({ 
  status = 'pending' 
}) => {
  
  const {
    procurements,
    isLoading,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    goToPage,
    hasNextPage,
    hasPreviousPage
  } = useOptimizedFinanceProcurements({ status, pageSize: 25 });

  const exportAllToCSV = () => {
    if (!procurements?.length) return;
    
    const headers = ['ID', 'Product', 'Source Type', 'Created Date', 'Status', 'Payment Term', 'Total Amount', 'Payment Due Date', 'Grading Type', 'Damage (kg)', 'Compensation (kg)'];
    const csvRows = [headers];
    
    procurements.forEach(procurement => {
      const quality = procurement.quality as any;
      const isPreliminary = quality?.grading_type === 'preliminary';
      const compensation = isPreliminary 
        ? (quality?.preliminary_compensation_kg || 0) 
        : (quality?.compensation_kg || 0);
      const damage = isPreliminary 
        ? (quality?.preliminary_damage_kg || 0) 
        : (quality?.damage_kg || 0);
        
      csvRows.push([
        procurement.id,
        procurement.product_name || 'N/A',
        procurement.source_type,
        format(new Date(procurement.created_at), 'yyyy-MM-dd'),
        procurement.status,
        procurement.finance?.payment_term_type || 'N/A',
        calculateTotalAmount(procurement).toString(),
        procurement.finance?.payment_due_date ? format(new Date(procurement.finance.payment_due_date), 'yyyy-MM-dd') : 'N/A',
        quality?.grading_type || 'N/A',
        damage.toFixed(2),
        compensation.toFixed(2)
      ]);
    });
    
    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${status}_transactions_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportAllToExcel = () => {
    if (!procurements?.length) return;

    const wb = XLSX.utils.book_new();

    // Summary
    const statusSummary = [
      [`${status.charAt(0).toUpperCase() + status.slice(1)} Transactions Summary`],
      ['Generated on:', format(new Date(), 'yyyy-MM-dd HH:mm:ss')],
      ['Total Transactions:', totalItems],
      ['Current Page:', currentPage],
      ['Total Pages:', totalPages]
    ];

    const summaryWS = XLSX.utils.aoa_to_sheet(statusSummary);
    XLSX.utils.book_append_sheet(wb, summaryWS, 'Summary');

    // Current page data
    const detailedHeaders = [
      'ID', 'Product', 'Source Type', 'Created Date', 'Status', 
      'Payment Term', 'Quantity', 'Price Per Unit', 'Transport Cost', 
      'Loading Cost', 'Agent Cost', 'Other Cost', 'Total Amount', 'Payment Due Date',
      'Grading Type', 'Damage (kg)', 'Compensation (kg)', 'Net Payable (kg)'
    ];
    
    const detailedData = [detailedHeaders];
    
    procurements.forEach(procurement => {
      const quality = procurement.quality as any;
      const isPreliminary = quality?.grading_type === 'preliminary';
      const compensation = isPreliminary 
        ? (quality?.preliminary_compensation_kg || 0) 
        : (quality?.compensation_kg || 0);
      const damage = isPreliminary 
        ? (quality?.preliminary_damage_kg || 0) 
        : (quality?.damage_kg || 0);
      const netPayable = (procurement.total_quantity || 0) - compensation;
      
      detailedData.push([
        procurement.id,
        procurement.product_name || 'N/A',
        procurement.source_type,
        format(new Date(procurement.created_at), 'yyyy-MM-dd'),
        procurement.status,
        procurement.finance?.payment_term_type || 'N/A',
        procurement.total_quantity?.toString() || '0',
        procurement.costs?.price_per_unit?.toString() || '0',
        procurement.costs?.transport_cost_per_unit?.toString() || '0',
        procurement.costs?.loading_unloading_cost_per_unit?.toString() || '0',
        procurement.costs?.agent_cost_per_unit?.toString() || '0',
        procurement.costs?.other_cost_per_unit?.toString() || '0',
        calculateTotalAmount(procurement).toString(),
        procurement.finance?.payment_due_date ? format(new Date(procurement.finance.payment_due_date), 'yyyy-MM-dd') : 'N/A',
        quality?.grading_type || 'N/A',
        damage.toFixed(2),
        compensation.toFixed(2),
        netPayable.toFixed(2)
      ]);
    });

    const detailedWS = XLSX.utils.aoa_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(wb, detailedWS, `${status.charAt(0).toUpperCase() + status.slice(1)} Page ${currentPage}`);

    XLSX.writeFile(wb, `${status}_transactions_page_${currentPage}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-center items-center py-8">
          <Loader2 className="animate-spin w-8 h-8 text-primary mr-2" />
          <span className="text-lg">Loading {status} procurements...</span>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="flex-1">
          <FinanceProcurementsSearch 
            searchTerm={searchTerm} 
            setSearchTerm={setSearchTerm} 
          />
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                Export Page
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={exportAllToCSV}>
                <FileText className="h-4 w-4 mr-2" />
                Export Current Page as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportAllToExcel}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export Current Page as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="text-sm text-muted-foreground mb-4">
        Showing {procurements.length} of {totalItems} {status} procurements (Page {currentPage} of {totalPages})
      </div>
      
      {procurements.length === 0 ? (
        <FinanceEmptyState />
      ) : (
        <>
          <div className="border rounded-md">
            <FinanceProcurementsTable 
              procurements={procurements}
              sortConfig={sortConfig}
              handleSort={handleSort}
            />
          </div>
          <FinancePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            totalItems={totalItems}
            startIndex={(currentPage - 1) * pageSize + 1}
            endIndex={Math.min(currentPage * pageSize, totalItems)}
            itemsPerPage={pageSize}
            onItemsPerPageChange={() => {}} // Not implemented for this optimized version
          />
        </>
      )}
    </div>
  );
};

export default OptimizedFinanceProcurementList;
